import {
  Label,
  LeftPart,
  ModelNameLabel,
  RightPart,
  Container,
  OrderPerModelLabel,
  IconWrapper,
  ToggleWrapper,
  ToggleGroup,
} from './BuyerModelsRow.styled';
import { useTranslation } from 'react-i18next';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import { useEffect } from 'react';
import {
  useToggleOrderBuyerModelExternally,
  useToggleOrderBuyerModelInternally,
} from './hooks';
import { queryClient } from 'index';
import { ReactQueryKeys } from 'services/api/reactQueryKeys';
import Icon from 'components/Icon/Icon';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { Trash } from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import { IBuyerModel } from 'types/BuyerModel';

interface IBuyerModelsRowProps {
  buyerModel: IBuyerModel;
  withoutLine: boolean;
  isLoadingParent: boolean;
  onDelete?: (id: number) => void;
}

const BuyerModelsRow = ({
  buyerModel,
  withoutLine,
  isLoadingParent,
  onDelete,
}: IBuyerModelsRowProps) => {
  const { t } = useTranslation();
  const { isMidTablet, isSmallTablet } = useBreakpointFlag();

  const {
    mutate: toggleOrderInternally,
    isLoading: isLoadingToggleOrderInternally,
    isSuccess: isSuccessToggleOrderInternally,
    reset: resetToggleOrderInternally,
  } = useToggleOrderBuyerModelInternally();

  const {
    mutate: toggleOrderExternally,
    isLoading: isLoadingToggleOrderExternally,
    isSuccess: isSuccessToggleOrderExternally,
    reset: resetToggleOrderExternally,
  } = useToggleOrderBuyerModelExternally();

  const isLoading =
    isLoadingToggleOrderInternally ||
    isLoadingToggleOrderExternally ||
    isLoadingParent;

  useEffect(() => {
    if (isSuccessToggleOrderInternally || isSuccessToggleOrderExternally) {
      queryClient.invalidateQueries([ReactQueryKeys.GET_BUYER_MODELS]);
      resetToggleOrderInternally();
      resetToggleOrderExternally();
    }
  }, [isSuccessToggleOrderInternally, isSuccessToggleOrderExternally]);

  return (
    <Container withoutLine={withoutLine}>
      <LeftPart>
        {buyerModel.model_name && (
          <ModelNameLabel>{buyerModel.model_name}</ModelNameLabel>
        )}
        <Label>
          {t('Model ID')}: {buyerModel.model_id}
        </Label>
        <Label>
          {t('Version ID')}: {buyerModel.model_version}
        </Label>
        {isSmallTablet && onDelete && (
          <IconWrapper>
            <Icon
              onClick={() => onDelete(buyerModel.id)}
              svg={Trash}
              color={COLORS.RED_100}
            />
          </IconWrapper>
        )}
      </LeftPart>
      <RightPart>
        <ToggleWrapper>
          <ToggleGroup>
            <OrderPerModelLabel>{t('Internal Ordering')}</OrderPerModelLabel>
            <ToggleSwitch
              disabled={isLoading}
              isChecked={buyerModel.can_order_internally}
              onClick={() => {
                if (isLoading) {
                  return;
                }
                if (buyerModel.can_order_internally) {
                  toggleOrderInternally({
                    modelId: buyerModel.id,
                    canOrderInternally: false,
                  });
                } else {
                  toggleOrderInternally({
                    modelId: buyerModel.id,
                    canOrderInternally: true,
                  });
                }
              }}
              size={isMidTablet ? 'small' : 'large'}
            />
          </ToggleGroup>
          <ToggleGroup>
            <OrderPerModelLabel>{t('External Ordering')}</OrderPerModelLabel>
            <ToggleSwitch
              disabled={isLoading}
              isChecked={buyerModel.can_order_externally}
              onClick={() => {
                if (isLoading) {
                  return;
                }
                if (buyerModel.can_order_externally) {
                  toggleOrderExternally({
                    modelId: buyerModel.id,
                    canOrderExternally: false,
                  });
                } else {
                  toggleOrderExternally({
                    modelId: buyerModel.id,
                    canOrderExternally: true,
                  });
                }
              }}
              size={isMidTablet ? 'small' : 'large'}
            />
          </ToggleGroup>
        </ToggleWrapper>
        {!isSmallTablet && onDelete && (
          <Icon
            onClick={() => onDelete?.(buyerModel.id)}
            svg={Trash}
            color={COLORS.RED_100}
          />
        )}
      </RightPart>
    </Container>
  );
};

export default BuyerModelsRow;
